import { isTenantModuleEnabled, moduleForPath } from './tenantModules';

export const PUBLIC_ROUTES = [
  '/venda',
  '/checkout',
  '/criar-conta',
  '/esqueci-senha',
  '/redefinir-senha'
];
export const PARTNER_PUBLIC_ROUTES = ['/parceiros', '/parceiros/login'];
export const PARTNER_PRIVATE_ROUTES = ['/parceiros/painel'];
export const PARTNER_PORTAL_ROUTES = [...PARTNER_PUBLIC_ROUTES, ...PARTNER_PRIVATE_ROUTES];
export const PLATFORM_OWNER_PUBLIC_ROUTES = ['/owner/login'];
export const PLATFORM_OWNER_PRIVATE_ROUTES = ['/owner'];
export const PLATFORM_OWNER_ROUTES = [...PLATFORM_OWNER_PUBLIC_ROUTES, ...PLATFORM_OWNER_PRIVATE_ROUTES];

export const ROUTE_DEPARTMENTS = {
  '/': 'vendas',
  '/inteligencia-publico': 'relatorios',
  '/pdv': 'vendas',
  '/caixa': 'financeiro',
  '/comandas-mobile': 'vendas',
  '/entregas': 'vendas',
  '/pedidos': 'vendas',
  '/mesas': 'vendas',
  '/faq-suporte': 'suporte',
  '/clientes': 'cadastros',
  '/fornecedores': 'cadastros',
  '/entregadores': 'cadastros',
  '/produtos': 'cadastros',
  '/tabelas-preco': 'cadastros',
  '/inventario': 'cadastros',
  '/catalogo-operacional': 'cadastros',
  '/categorias-produtos': 'cadastros',
  '/tabela-frete': 'cadastros',
  '/colaboradores': 'pessoas',
  '/ponto': 'pessoas',
  '/agenda-comissoes': 'pessoas',
  '/ponto-kiosk': 'pessoas',
  '/base-conhecimento': 'pessoas',
  '/relatorios': 'relatorios',
  '/consultas': 'relatorios',
  '/financeiro': 'financeiro',
  '/financeiro-organizador': 'financeiro',
  '/dre': 'financeiro',
  '/crediario': 'financeiro',
  '/asaas-assinaturas': 'configuracoes',
  '/financeiro-configuracoes': 'financeiro',
  '/ordens-compra': 'financeiro',
  '/resumo-conta': 'configuracoes',
  '/loja-extensoes': 'configuracoes',
  '/configuracoes': 'configuracoes',
  '/fiscal': 'configuracoes',
  '/espaco-contador': 'contabilidade',
  '/nfe-mg': 'configuracoes',
  '/nfse-itabira': 'configuracoes',
  '/integracoes': 'configuracoes',
  '/dispositivos': 'configuracoes',
  '/api-desenvolvedores': 'configuracoes',
  '/conecta-clube': 'configuracoes',
  '/autorizar-conecta': 'configuracoes',
  '/wbuy': 'configuracoes',
  '/correios': 'configuracoes',
  '/pagarme': 'financeiro',
  '/email-resend': 'configuracoes',
  '/midia-publicidade': 'configuracoes',
  '/central-novidades': 'configuracoes',
  '/banners-parceiros': 'configuracoes',
  '/instalador-local': 'configuracoes'
};

export const OWNER_ROUTES = ['/owner'];
export const OWNER_ONLY_ROUTES = ['/central-novidades', '/banners-parceiros'];
export const PLATFORM_ONLY_ROUTES = [
  '/asaas-assinaturas',
  '/api-desenvolvedores',
  '/email-resend',
  '/integracoes',
  '/loja-extensoes',
  '/midia-publicidade',
  '/midia-player',
  '/resumo-conta'
];

export function getUserRole(user) {
  return user?.perfil_acesso || user?.cargo || 'colaborador';
}

export function getUserDepartments(user) {
  if (!user) return [];
  if (Array.isArray(user.departamentos_acesso)) return user.departamentos_acesso;
  if (typeof user.departamentos_acesso === 'string' && user.departamentos_acesso.trim()) {
    try {
      const parsed = JSON.parse(user.departamentos_acesso);
      if (Array.isArray(parsed)) return parsed;
    } catch (error) {
      return user.departamentos_acesso.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }
  return [];
}

export function canAccessPath(user, path) {
  // O portal de parceiros possui identidade e sessao proprias; nunca herda o
  // usuario do estabelecimento, mesmo quando ele e owner ou administrador.
  if (PARTNER_PORTAL_ROUTES.includes(path)) return false;
  if (PUBLIC_ROUTES.includes(path)) return true;
  if (!user) return false;

  // Entitlement e perfil sao fronteiras independentes. O owner do tenant
  // pode administrar tudo que foi habilitado para a empresa, mas nao reabre
  // um modulo suspenso pela central.
  if (!isTenantModuleEnabled(user, moduleForPath(path))) return false;

  const role = getUserRole(user);
  if (role === 'owner') return true;
  if (OWNER_ROUTES.includes(path)) return false;
  if (OWNER_ONLY_ROUTES.includes(path)) return false;
  if (PLATFORM_ONLY_ROUTES.includes(path)) return false;
  if (path === '/faq-suporte') return true;
  if (role === 'contador') return path === '/espaco-contador' || path === '/fiscal' || path === '/pagarme' || path === '/faq-suporte';
  if (role === 'admin') return true;
  if (role === 'vendedor') return path === '/pdv' || path === '/caixa' || path === '/comandas-mobile' || path === '/faq-suporte';
  if (role === 'entregador') return path === '/entregas' || path === '/faq-suporte';
  if (path === '/pagarme') return false;

  const department = ROUTE_DEPARTMENTS[path];
  if (!department) return false;

  const departments = getUserDepartments(user);
  return departments.includes(department);
}

export function getDefaultPathForUser(user) {
  const role = getUserRole(user);
  const preferred = {
    // `/owner` e uma rota HTTP exclusiva do control plane global. O owner do
    // tenant continua dentro do HashRouter operacional.
    owner: ['/', '/faq-suporte'],
    admin: ['/', '/faq-suporte'],
    vendedor: ['/pdv', '/caixa', '/faq-suporte'],
    entregador: ['/entregas', '/faq-suporte'],
    contador: ['/espaco-contador', '/fiscal', '/faq-suporte']
  }[role];
  if (preferred) return preferred.find((path) => canAccessPath(user, path)) || '/sem-acesso';

  const departments = getUserDepartments(user);
  const first = Object.entries(ROUTE_DEPARTMENTS).find(([path, department]) => (
    departments.includes(department) && canAccessPath(user, path)
  ));
  return first?.[0] || '/sem-acesso';
}

export function hashToPath(hash) {
  const cleaned = String(hash || '#/').replace(/^#/, '') || '/';
  return cleaned.split('?')[0];
}

export function getPartnerPortalPath(locationLike = {}) {
  const pathname = String(locationLike.pathname || '/').replace(/\/+$/, '') || '/';
  if (PARTNER_PORTAL_ROUTES.includes(pathname)) return pathname;
  const hashPath = hashToPath(locationLike.hash || '');
  return PARTNER_PORTAL_ROUTES.includes(hashPath) ? hashPath : null;
}

// O control plane usa somente rotas HTTP profundas. Nao le o hash para nao
// capturar o legado local #/owner do Electron nem misturar identidade de
// plataforma com a sessao operacional de um tenant.
export function getPlatformOwnerPath(locationLike = {}) {
  const pathname = String(locationLike.pathname || '/').replace(/\/+$/, '') || '/';
  return PLATFORM_OWNER_ROUTES.includes(pathname) ? pathname : null;
}
